import axios from 'axios'
import ms from 'ms'

const AUTHAPI_BASEURL = process.env.VUE_APP_AUTHAPI_BASEURL
const IDTOKEN_REFRESH_THRESHOLD = Math.floor(ms(String(process.env.VUE_APP_IDTOKEN_REFRESH_THRESHOLD)) / 1000)
const SESSION_EXPIRES_IN = Math.floor(ms(String(process.env.VUE_APP_SESSION_EXPIRES_IN)) / 1000)
const DEBUG = parseInt(process.env.VUE_APP_DEBUG)

// IDトークン自動更新用のタイマー
let idTokenRefreshTimer = null

const state = {
  idToken: '',
  refreshToken: '',
  expiresIn: 0,
  tokenExpiresAt: 0,
  sessionTimeOutAt: 0,
  localId: '',
  email: '',
  displayName: '',
  userId: null,
  clientId: null,
  affiliationId: null,
  // safeiクライアントID
  safieClientId: '',
  // safei認証コード
  safieAuthenticationFlag: '',
  // 2FAパスコード
  passcode: '',
  // 2FAブラウザフィンガープリント
  bfp: ''
}

const getters = {
  idToken: state => () => state.idToken,
  refreshToken: state => () => state.refreshToken,
  localId: state => state.localId,
  email: state => state.email,
  displayName: state => state.displayName,
  userId: state => state.userId,
  clientId: state => state.clientId,
  affiliationId: state => state.affiliationId,

  sessionRemainIn: state => () => (state.sessionTimeOutAt - Math.floor(Date.now() / 1000)),

  isLoggedIn: state => () => !!state.idToken, // idToken有無により判定
  isAuthenticated: state => () => (
    (!!state.idToken) &&
    (state.sessionTimeOutAt - Math.floor(Date.now() / 1000)) >= 0
  ), // false: 未ログイン状態、またはセッションタイムアウトの場合
  isSessionTimedOut: state => () => (
    (state.sessionTimeOutAt - Math.floor(Date.now() / 1000)) < 0
  ),

  // safeiクライアントID
  safieClientId: state => () => state.safieClientId,
  // safeiリダイレクトURL
  safieAuthenticationFlag: state => () => state.safieAuthenticationFlag,

  // 2FAパスコード
  passcode: state => state.passcode,
  // 2FAブラウザフィンガープリント
  bfp: state => state.bfp,
}

const mutations = {
  /**
   * サインイン
   * @param {Object} payload サインインAPIレスポンス
   */
  signIn(state, payload) {
    const timeStamp = new Date().getTime()
    state.idToken = payload.idToken
    state.refreshToken = payload.refreshToken
    state.expiresIn = parseInt(payload.expiresIn)
    state.tokenExpiresAt = parseInt(payload.expiresAt)
    state.sessionTimeOutAt = Math.floor(Date.now() / 1000) + SESSION_EXPIRES_IN
    state.localId = payload.localId
    state.email = payload.email
    state.displayName = payload.displayName
    state.userId = payload.userId
    state.clientId = payload.clientId
    state.affiliationId = payload.affiliationId
    // safeiクライアントID
    state.safieClientId = payload.safieClientId
    // safei認証コード
    state.safieAuthenticationFlag = payload.safieAuthenticationFlag
    // 2FAパスコード
    state.passcode = payload.passcode
    // 2FAブラウザフィンガープリント
    state.bfp = payload.bfp
  },

  /**
   * サインアウト
   */
  signOut(state) {
    state.idToken = ''
    state.refreshToken = ''
    state.expiresIn = 0
    state.tokenExpiresAt = 0
    state.sessionTimeOutAt = 0
    state.localId = ''
    state.email = ''
    state.displayName = ''
    state.userId = null
    state.clientId = null
    state.affiliationId = null
    // safeiクライアントID
    state.safieClientId = ''
    // safei認証画面表示フラグ
    state.safieAuthenticationFlag
    // 2FAパスコード
    state.passcode = ''
    // 2FAブラウザフィンガープリント
    state.bfp = ''
  },

  /**
   * トークンリフレッシュ
   * @param {*} payload IDトークンリフレッシュAPIレスポンス
   */
  refreshToken(state, payload) {
    state.idToken = payload.idToken
    state.expiresIn = parseInt(payload.expiresIn)
    state.tokenExpiresAt = parseInt(payload.expiresAt)
  },

}

const debug = msg => {
  if (DEBUG === 1)
    console.log(msg)
}

const actions = {
  /**
   * ログインアクション
   */
  async login({commit, dispatch}, {userId, password, passcode, bfp}) {
    try {
      const payload = await signIn(userId, password, passcode, bfp)
      if (!payload.error) {
        if (payload.initPasswordFlag) {
          // 初期パスワード時
          return 2
        }
        if (payload.passcodeRequired) {
          // 2FA有効時
          return 3
        }

        // サインインミューテーション実行
        commit('signIn', payload)
        // IDトークンリフレッシュアクション実行
        dispatch('setIdTokenRefreshTimer')
        return 0
      } else {
        // emailまたはパスワード不正時
        return 1
      }
    } catch (err) {
      return 9
    }
  },

  /**
   * ログアウトアクション
   */
  async logout({commit, dispatch}) {
    try {
      await signOut()
    } catch (err) {
      debug(err)
    }
    commit('signOut')
    // IDトークンリフレッシュの自動更新をストップ
    dispatch('clearIdTokenRefreshTimer')
  },

  /**
   * IDトークンリフレッシュアクション<br/>
   * リフレッシュトークンからIDトークンを取得する<br/>
   * ※セッションタイムアウトしている場合、トークンリフレッシュ用タイマーをクリアする
   */
  async refreshIdToken({state, getters, commit}) {
    // IDトークンの再取得
    const exchangeIdToken = async refreshToken => {
      const $http = axios.create({
        baseURL: AUTHAPI_BASEURL,
        headers: {'Content-Type': 'application/json'},
        timeout: 15000
      })

      const payload = await $http.post(
        '/token', {
          refreshToken: refreshToken
        })

      return payload.data.data
    }
    const NOOP = () => {}

    try {
      const payload = await exchangeIdToken(getters.refreshToken())
      if (!payload.error) {
        commit('refreshToken', payload)
      }
    } catch (err) {
      debug(err)
    }
  },

  /**
   * IDトークン自動更新用のタイマー開始設定<br/>
   * 短周期でIDトークンをチェックし、有効期限切れになる前にトークンのリフレッシュ（再取得）を行う
   */
  setIdTokenRefreshTimer({state, getters, dispatch}) {

    debug('setIdTokenRefreshTimer:: === start')

    let isInProgress = false;

    if (idTokenRefreshTimer) {
      dispatch('clearIdTokenRefreshTimer')
    }

    idTokenRefreshTimer = setInterval(async ()=>{
      debug('idTokenRefreshTimer:: === start')
      if (isInProgress) return
      isInProgress = true
      try {
        debug(`idTokenRefreshTimer:: is session alive? (remainSec=${getters.sessionRemainIn()})`)
        // セッションタイムアウトの判定
        if (getters.isSessionTimedOut()) {
          debug(`idTokenRefreshTimer:: session is dead!`)
          // IDトークンリフレッシュの自動更新をストップ
          dispatch('clearIdTokenRefreshTimer')
          return
        }
        debug(`idTokenRefreshTimer:: session is alive`)

        // IDトークンの残有効時間（秒）が閾値を下回った場合はトークン再取得
        const remainSec = state.tokenExpiresAt - Math.floor(Date.now() / 1000)
        debug(`idTokenRefreshTimer:: idtoken remainSec=${remainSec}, threshold=${IDTOKEN_REFRESH_THRESHOLD}`)
        if (remainSec < IDTOKEN_REFRESH_THRESHOLD) {
          debug(`idTokenRefreshTimer:: idtoken needs refresh`)
          await dispatch('refreshIdToken')
          debug(`idTokenRefreshTimer:: idtoken is refreshed!`)
        }
      } finally {
        isInProgress = false
        debug('idTokenRefreshTimer:: === end')
      }
    }, 1000)

    debug('setIdTokenRefreshTimer:: === end')
  },

  /**
   * IDトークン自動更新用のタイマーのクリア処理
   */
  clearIdTokenRefreshTimer() {
    if (idTokenRefreshTimer)
      clearInterval(idTokenRefreshTimer)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}

/**
 * サインイン処理
 * @param {string} email メールアドレス（ユーザーID）
 * @param {string} password パスワード
 * @param {string} passcode 2FAパスコード
 * @param {string} bfp 2FAブラウザフィンガープリント
 * @returns {Object} サインインAPIレスポンス
 */
const signIn = async (email, password, passcode, bfp) => {
  const $http = axios.create({
    baseURL: AUTHAPI_BASEURL,
    headers: {'Content-Type': 'application/json'},
    timeout: 15000
  })

  const parameters = {
    loginId: email,
    password: password,
    passcode: passcode,
    bfp: bfp,
  }

  const payload = await $http.post('/login', parameters)
  return payload.data.data
}

/**
 * サインアウト処理
 */
const signOut = async () => {
  // TODO: 必要に応じて実装（サインアウト時刻記録、監査ログ出力を行う場合など）
}
